import { FC, HTMLAttributes, useEffect, useRef, useState } from "react";
import { Header } from "@tanstack/react-table";
import classNames from "classnames";
import { Hint } from '@redoc/ui-kit';
import { Headline } from "src/UI";
import { TableSort } from "../../..";
import SortButton from "../../SortBtn/SortBtn";
import Resizer from "../../Resizer/Resizer";
import { SortStateType, SortTypes } from "../../../types";
import st from "./TitleCell.module.scss";

interface IHeaderTitleCellProps extends HTMLAttributes<HTMLTableCellElement> {
	header: Header<any, any>;
	minColumnSize?: number;
	colSpan?: number;
	width: number | "100%";
	sort?: TableSort;
};

const HeaderTitleCell: FC<IHeaderTitleCellProps> = ( props ) => {

	const { header, minColumnSize = 100, colSpan, width, sort } = props;

	const spanRef = useRef<HTMLHeadingElement>(null);
	const [ isOverflow, setIsOverflow ] = useState<boolean>(true);

	useEffect(() => {
		checkOverflow();
	}, [width] );

	/** Проверка на переполнение */
	const checkOverflow = () => {
		if ( !spanRef?.current ) return true;
		const overflow = spanRef.current.scrollWidth <= spanRef.current.clientWidth;
		setIsOverflow(overflow);
		return spanRef.current.scrollWidth <= spanRef.current.clientWidth;
	};

	const isResizing = header.column.getIsResizing();
	const isSortable = header.column.getCanSort();
	const headerCellClasses = classNames( st.wrapper, {
		[st.sortable]: isSortable
	});

	/** Функция изменения состояния сортировки */
	const onSortChange = ( newState: SortStateType ) => {
		if ( !!sort ) {
			sort.setState( { column: newState.columnId, sorttype: newState.type } );
		}
	};

	/** Вычисление следующего состояния и отправка его в таблицу */
	const onSortToggleHandler = ( colId: string ) => {
		if ( !isSortable ) return;
		const newSate = {
			columnId: colId, 
			type: getNextSortDir( colId )
		};
		onSortChange( newSate );
	};

	/** Вычисляет следующие состояние сортировки  */
	const getNextSortDir = ( colId: string ): SortTypes => {
		if ( sort?.state.column !== colId )
			return "ASC";
		else if ( !sort?.state?.sorttype )
			return "ASC";
		else if ( sort?.state.sorttype === "ASC" )
			return "DESC";
		else if ( sort?.state.sorttype === "DESC" )
			return "ASC";
	};

	const renderSort = () => {
		if ( !!sort ) {
			return (
				<SortButton 
					columnId={header.id}
					disabled={!header.column.getCanSort()}
					state={ { columnId: sort?.state.column!, type: sort?.state.sorttype }}
				/>
			);
		}
		return <span className={st.sortPlaceholder} aria-hidden="true" />;
	};

	const title = ( header.getContext().header.column.columnDef as any).title;
	return (
		<th
			colSpan={ !!colSpan ? colSpan : header.colSpan }
			className={classNames( st.MainHeaderCell, {[st.isResizing]: isResizing})}
		>
		{
			!header.isPlaceholder &&
			<div
				className={headerCellClasses}
				onClick={() => onSortToggleHandler(header.id)}
				style={{ minWidth: (minColumnSize + 'px'), width }}
			>
				{ renderSort() }
				<Headline type="xsmall" className={st.title} ref={spanRef}>
					<Hint messageText={title} placement='bottom' disabled={isOverflow}>
						<span>{title}</span>
					</Hint>
				</Headline>
			</div>
		}
		{
			header.column.getCanResize() && 
			<Resizer
				resizeHandler={header.getResizeHandler()}
				isResizing={isResizing}
			/>
		}
		</th>
	);
};

export default HeaderTitleCell;
